import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { SEND_FEEDBACK_TEMPMEE, sendTempmeeFeedback } from '../../actions';
import { TextAreaField } from '~/components/shared/forms';
import { Button, Screen, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  BackButton,
  Box,
  Center,
  CenteredSpinner,
  Column,
  EmojiSelection,
} from '~/commonComponents';
import useLoading from '../../hooks/useLoading';
import { useTrackEvent } from '../../hooks/useTrackEvent';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { NumberUtils } from '~/utils/NumberUtils';

export const SendFeedback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const { isLoading } = useLoading([SEND_FEEDBACK_TEMPMEE]);
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
  const [selectedEmojiNumber, setSelectedEmojiNumber] = useState<number | null>(null);
  const [feedbackNote, setFeedbackNote] = useState('');

  const handleSubmit = () => {
    // Track feedback submission event
    trackEvent(POSTHOG_EVENTS.OFFICE_FEEDBACK_SUBMITTED, {
      rating: selectedEmojiNumber,
      ratingDescription: selectedEmoji,
      hasMessage: !!feedbackNote,
    });

    dispatch(
      sendTempmeeFeedback({
        rating: selectedEmojiNumber,
        ratingDesc: selectedEmoji,
        message: feedbackNote,
      }),
    );
  };

  if (isLoading) {
    return (
      <Screen variant="standard">
        <Screen.Header start={<BackButton onClick={() => navigate(-1)} />} title="Send Feedback" />
        <Screen.Body inset>
          <CenteredSpinner minHeight={400} />
        </Screen.Body>
      </Screen>
    );
  }

  return (
    <Screen variant="standard">
      <Screen.Header start={<BackButton onClick={() => navigate(-1)} />} title="Send Feedback" />
      <Screen.Body inset>
        <Column
          paddingTop={32}
          paddingBottom={32}
          backgroundColor={colors.shades[0]}
          marginTop={20}
          style={{
            borderRadius: 8,
            boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Center>
            <Typography as="span" size="heading_3" bold color={colors.primary[500]}>
              How do you rate GoTu?
            </Typography>
            <Typography as="span" textAlign="center" style={{ marginTop: 20 }}>
              Let us know how we are doing! Your feedback helps make GoTu better for everyone.
              <br />
              Got thoughts? Share them with us below!
            </Typography>

            <Box marginTop={40} marginBottom={32}>
              <EmojiSelection
                onSelect={({ title, value }) => {
                  setSelectedEmoji(title);
                  setSelectedEmojiNumber(value);
                }}
              />
            </Box>

            <Column width="100%" marginTop={20}>
              <Typography as="span" size="body_large" bold style={{ marginBottom: 8 }}>
                Anything we could do better?
              </Typography>
              <TextAreaField
                height={80}
                style={{ maxWidth: 900 }}
                placeholder="Tell us what you like, or what you didn’t"
                onChange={(e) => setFeedbackNote(e.target.value)}
                value={feedbackNote}
              />
            </Column>
            <Typography
              as="span"
              color={colors.neutral[500]}
              textAlign="center"
              style={{ marginBottom: 12, marginTop: 32 }}
            >
              For help with your account or posted jobs, contact your
              <br />
              regional account manager, or call {NumberUtils.getCSPhone().formatted}.
            </Typography>
          </Center>
        </Column>

        <Center marginTop={56}>
          <Button size="lg" disabled={!selectedEmoji} onClick={handleSubmit}>
            Send Feedback
          </Button>
        </Center>
      </Screen.Body>
    </Screen>
  );
};
